// src/components/home/SearchWidget.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PublicAPI } from '../../api/public';

const Container = styled.section`
  background: var(--card);
  border: 1px solid var(--bdr);
  border-radius: 18px;
  box-shadow: var(--shadow);
  padding: 24px;
  margin: 20px 0;
  
  @media (max-width: 768px) {
    padding: 20px 16px;
    margin: 16px 0;
  }
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  font-size: clamp(20px, 4vw, 28px);
  font-weight: 800;
  text-align: center;
  color: var(--fg);
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  
  label {
    font-size: 14px;
    font-weight: 600;
    color: var(--muted);
  }
  
  select, input {
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid var(--bdr);
    background: var(--card);
    color: var(--fg);
    font-size: 16px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(176, 141, 87, 0.15);
    }
  }
`;

const RangeField = styled(Field)`
  .range-inputs {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 8px;
    align-items: center;
    
    span {
      font-size: 14px;
      color: var(--muted);
      text-align: center;
      min-width: 16px;
    }
    
    input {
      min-width: 0; /* Позволяет input сжиматься */
      width: 100%;
      
      &::placeholder {
        font-size: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
  
  @media (max-width: 480px) {
    .range-inputs {
      gap: 6px;
      
      input {
        font-size: 14px;
        padding: 10px 8px;
        
        &::placeholder {
          font-size: 13px;
        }
      }
      
      span {
        font-size: 12px;
        min-width: 12px;
      }
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 8px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 14px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--bdr);
  
  &.primary {
    background: var(--primary);
    color: var(--primaryFg);
    border-color: transparent;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(176, 141, 87, 0.3);
    }
  }
  
  &.secondary {
    background: var(--card);
    color: var(--fg);
    
    &:hover {
      background: var(--primary);
      color: var(--primaryFg);
    }
  }
`;

export default function SearchWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [districts, setDistricts] = React.useState([]);
  const [propertyTypes, setPropertyTypes] = React.useState([]);
  const [dealTypes, setDealTypes] = React.useState([]);
  
  // Состояние формы
  const [propertyType, setPropertyType] = React.useState('');
  const [dealType, setDealType] = React.useState('');
  const [district, setDistrict] = React.useState('');
  const [bedrooms, setBedrooms] = React.useState('');
  const [priceMin, setPriceMin] = React.useState('');
  const [priceMax, setPriceMax] = React.useState('');
  const [areaMin, setAreaMin] = React.useState('');
  const [areaMax, setAreaMax] = React.useState('');

  // Загрузка справочников
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [d, pt, dt] = await Promise.all([
          PublicAPI.districts(),
          PublicAPI.propertyTypes(),
          PublicAPI.dealTypes(),
        ]);
        if (!mounted) return;
        setDistricts(Array.isArray(d?.districts) ? d.districts : Array.isArray(d) ? d : []);
        setPropertyTypes(Array.isArray(pt?.property_types) ? pt.property_types : []);
        setDealTypes(Array.isArray(dt?.deal_types) ? dt.deal_types : []);
      } catch (error) {
        console.error('Error loading search data:', error);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Собираем параметры поиска
    const params = new URLSearchParams();
    
    if (propertyType) params.set('property_type', propertyType);
    if (dealType) params.set('deal_type', dealType);
    if (district) params.set('district_id', district);
    if (bedrooms) params.set('bedrooms', bedrooms);
    if (priceMin) params.set('price_min', priceMin);
    if (priceMax) params.set('price_max', priceMax);
    if (areaMin) params.set('area_min', areaMin);
    if (areaMax) params.set('area_max', areaMax);
    
    // Переходим на страницу поиска с параметрами
    const searchUrl = `/search${params.toString() ? `?${params.toString()}` : ''}`;
    navigate(searchUrl);
  };

  const handleMapSearch = () => {
    // Пока дублируем функциональность кнопки "Найти"
    const params = new URLSearchParams();
    
    if (propertyType) params.set('property_type', propertyType);
    if (dealType) params.set('deal_type', dealType);
    if (district) params.set('district_id', district);
    if (bedrooms) params.set('bedrooms', bedrooms);
    if (priceMin) params.set('price_min', priceMin);
    if (priceMax) params.set('price_max', priceMax);
    if (areaMin) params.set('area_min', areaMin);
    if (areaMax) params.set('area_max', areaMax);
    
    // Переходим на страницу поиска (пока дублируем)
    const searchUrl = `/search${params.toString() ? `?${params.toString()}` : ''}`;
    navigate(searchUrl);
  };

  return (
    <Container>
      <Title>{t('search.title')}</Title>
      
      <Form onSubmit={handleSearch}>
        <Field>
          <label>{t('search.propertyType')}</label>
          <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
            <option value="">{t('search.any')}</option>
            {propertyTypes.map(pt => (
              <option key={pt.id} value={pt.slug}>
                {pt.name}
              </option>
            ))}
          </select>
        </Field>

        <Field>
          <label>{t('search.dealType')}</label>
          <select value={dealType} onChange={(e) => setDealType(e.target.value)}>
            <option value="">{t('search.any')}</option>
            {dealTypes.map(dt => (
              <option key={dt.id} value={dt.slug}>
                {dt.name}
              </option>
            ))}
          </select>
        </Field>

        <Field>
          <label>{t('search.district')}</label>
          <select value={district} onChange={(e) => setDistrict(e.target.value)}>
            <option value="">{t('search.any')}</option>
            {districts.map(d => (
              <option key={d.id} value={d.id}>
                {d.name_ru || d.name || d.name_en || d.slug}
              </option>
            ))}
          </select>
        </Field>

        <Field>
          <label>{t('search.bedrooms')}</label>
          <select value={bedrooms} onChange={(e) => setBedrooms(e.target.value)}>
            <option value="">{t('search.anyBedrooms')}</option>
            {[1, 2, 3, 4, 5].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </Field>

        <RangeField>
          <label>{t('search.price')}</label>
          <div className="range-inputs">
            <input
              type="number"
              placeholder={t('search.from')}
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
            />
            <span>—</span>
            <input
              type="number"
              placeholder={t('search.to')}
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
            />
          </div>
        </RangeField>

        <RangeField>
          <label>{t('search.area')}</label>
          <div className="range-inputs">
            <input
              type="number"
              placeholder={t('search.from')}
              value={areaMin}
              onChange={(e) => setAreaMin(e.target.value)}
            />
            <span>—</span>
            <input
              type="number"
              placeholder={t('search.to')}
              value={areaMax}
              onChange={(e) => setAreaMax(e.target.value)}
            />
          </div>
        </RangeField>
      </Form>

      <ButtonGroup>
        <Button type="button" className="secondary" onClick={handleMapSearch}>
          {t('search.onMap')}
        </Button>
        <Button type="submit" className="primary" onClick={handleSearch}>
          {t('search.find')}
        </Button>
      </ButtonGroup>
    </Container>
  );
}